// Shared sticky header + footer used by every non-Home page.
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Mail, Phone, ArrowRight, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const BOOKING_URL = "https://api.leadconnectorhq.com/widget/bookings/jc3-strategy-call";
const EMAIL = "jace@jc3wealthlegacy.com";
const PHONE = "470-888-7331";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "Pricing", to: "/pricing" },
  { label: "How It Works", to: "/#process" },
  { label: "About", to: "/#about" },
  { label: "FAQ", to: "/#faq" },
];

const FOOTER_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "Pricing", to: "/pricing" },
  { label: "About", to: "/#about" },
  { label: "Dashboard", to: "/dashboard" },
  { label: "Contact", to: `mailto:${EMAIL}`, external: true },
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Financial Privacy Notice", to: "/privacy/glba" },
  { label: "Terms of Service", to: "/terms" },
  { label: "Refund Policy", to: "/refund" },
];

export default function BrandShell({ children, dashboardChrome = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user, logout } = useAuth();
  const loc = useLocation();
  const year = new Date().getFullYear();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [loc.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-jc-white">
      <header
        data-testid="sticky-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || dashboardChrome ? "bg-jc-black/90 backdrop-blur-md border-b border-jc-gold/15" : "bg-jc-black/60 backdrop-blur-sm"
        }`}
      >
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-mark" className="flex items-center gap-3">
            <img src="/logo-transparent.png" alt="JC3 Wealth Legacy" className="h-10 w-10 object-contain"/>
            <span className="font-display text-lg md:text-xl tracking-[0.18em] text-jc-white">
              JC3 <span className="text-jc-gold">·</span> WEALTH LEGACY
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.28em] text-jc-muted">
            {NAV.map((l) => (
              <a key={l.label} href={l.to} className="jc-link-underline hover:text-jc-white">
                {l.label}
              </a>
            ))}
            {user && user.role ? (
              <Link to={user.role === "admin" ? "/dashboard/admin" : "/dashboard/client"} className="jc-link-underline hover:text-jc-white inline-flex items-center gap-2">
                <LayoutDashboard size={12} /> Dashboard
              </Link>
            ) : (
              <Link to="/dashboard" className="jc-link-underline hover:text-jc-white">Dashboard</Link>
            )}
          </nav>
          <div className="flex items-center gap-3">
            {user && user.role ? (
              <button
                onClick={logout}
                data-testid="header-logout"
                className="hidden md:inline-flex jc-btn jc-btn-outline !py-2.5 !px-4 !text-xs"
              >
                <LogOut size={14} /> LOG OUT
              </button>
            ) : (
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-book-cta"
                className="hidden md:inline-flex jc-btn jc-btn-primary !py-2.5 !px-4 !text-xs"
              >
                BOOK A CALL
              </a>
            )}
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
              className="lg:hidden inline-flex items-center justify-center h-10 w-10 border border-jc-gold/40 text-jc-gold hover:bg-jc-gold hover:text-jc-black transition-colors"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* mobile drawer */}
      <div
        data-testid="mobile-drawer"
        className={`fixed inset-0 z-[60] transition-opacity duration-300 ${drawerOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!drawerOpen}
      >
        <div className="absolute inset-0 bg-jc-black/95 backdrop-blur-md" onClick={() => setDrawerOpen(false)} />
        <div
          className={`absolute inset-y-0 right-0 w-full max-w-sm bg-jc-navy border-l border-jc-gold/30 p-8 flex flex-col transition-transform duration-300 ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between mb-12">
            <span className="font-display text-lg tracking-[0.18em] text-jc-white">JC3 <span className="text-jc-gold">·</span> MENU</span>
            <button onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-close" aria-label="Close menu" className="h-10 w-10 inline-flex items-center justify-center border border-jc-gold/40 text-jc-gold hover:bg-jc-gold hover:text-jc-black transition-colors">
              <X size={18} />
            </button>
          </div>
          <nav className="flex-1 flex flex-col gap-1">
            {NAV.map((l) => (
              <a key={l.label} href={l.to} className="py-4 font-display text-2xl tracking-[0.1em] text-jc-white border-b border-jc-gold/10 hover:text-jc-gold transition-colors">
                {l.label.toUpperCase()}
              </a>
            ))}
            <Link to={user && user.role ? (user.role === "admin" ? "/dashboard/admin" : "/dashboard/client") : "/dashboard"} className="py-4 font-display text-2xl tracking-[0.1em] text-jc-white border-b border-jc-gold/10 hover:text-jc-gold transition-colors">
              DASHBOARD
            </Link>
          </nav>
          {user && user.role ? (
            <button onClick={logout} className="mt-6 jc-btn jc-btn-outline w-full !py-4">
              <LogOut size={16} /> LOG OUT
            </button>
          ) : (
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="mt-6 jc-btn jc-btn-primary w-full !py-4">
              BOOK A CALL <ArrowRight size={16} strokeWidth={2.5} />
            </a>
          )}
        </div>
      </div>

      <main className="flex-1 pt-20">{children}</main>

      <footer data-testid="footer-section" className="bg-jc-black text-jc-white border-t border-jc-gold/20">
        <div className="mx-auto max-w-7xl px-6 md:px-10 py-16 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <img src="/logo-transparent.png" alt="JC3 Wealth Legacy" className="h-12 w-12 object-contain"/>
              <span className="font-display text-xl tracking-[0.18em] text-jc-white">JC3 <span className="text-jc-gold">·</span> WEALTH LEGACY</span>
            </div>
            <p className="mt-3 font-mono text-[10.5px] uppercase tracking-[0.22em] text-jc-gold/85">
              Veteran-Owned Business
            </p>
            <p className="mt-4 font-body text-base text-jc-muted leading-relaxed max-w-xs">
              The Gatekeeper's Playbook. Now in Your Hands.
            </p>
          </div>
          <div className="md:col-span-4">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-jc-gold mb-5">Navigate</div>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 font-body text-base">
              {FOOTER_LINKS.map((l) => {
                const tid = `footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
                return (
                  <li key={l.label}>
                    {l.external ? (
                      <a href={l.to} data-testid={tid} className="jc-link-underline text-jc-white/85 hover:text-jc-gold">{l.label}</a>
                    ) : (
                      <Link to={l.to} data-testid={tid} className="jc-link-underline text-jc-white/85 hover:text-jc-gold">{l.label}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="md:col-span-4">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-jc-gold mb-5">Contact</div>
            <ul className="space-y-4 font-body text-base">
              <li><a href={`mailto:${EMAIL}`} data-testid="footer-email" className="inline-flex items-center gap-3 text-jc-white/85 hover:text-jc-gold transition-colors"><Mail size={16} className="text-jc-gold" />{EMAIL}</a></li>
              <li><a href={`tel:${PHONE.replace(/[^0-9]/g, "")}`} data-testid="footer-phone" className="inline-flex items-center gap-3 text-jc-white/85 hover:text-jc-gold transition-colors"><Phone size={16} className="text-jc-gold" />{PHONE}</a></li>
            </ul>
          </div>
        </div>
        <div className="jc-divider" />
        <div className="mx-auto max-w-7xl px-6 md:px-10 pt-8 pb-5">
          <p className="font-body text-[12px] leading-[1.7] text-jc-muted max-w-3xl">
            JC3 Wealth Legacy Group LLC provides financial education and consulting services
            only. We are not a credit repair organization as defined by the Credit Repair
            Organizations Act. We are not a licensed lender, loan broker, securities dealer,
            or registered investment advisor. All services are educational in nature. Results
            are not guaranteed and vary by individual.
          </p>
        </div>
        <div className="mx-auto max-w-7xl px-6 md:px-10 pt-4 pb-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 border-t border-jc-gold/15">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-jc-muted">
            © {year} JC3 Wealth Legacy Group LLC · Atlanta, Georgia
          </p>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-jc-gold/80">◆ Veteran-Owned Business</p>
        </div>
      </footer>
    </div>
  );
}
